import React, { useEffect, useRef, useState } from "react";
import { Star } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * The UI/UX survey: five stars, an optional comment, Send and Not now (T15-M3).
 *
 * Every visible string is one of the seven keys Jorge approved; this component
 * invents none. The stars carry no copy either — the group is labelled with the
 * modal's own title and each star is labelled with its number, which is how the
 * review modal in JobDetail already does it and is why no eighth key was
 * needed.
 *
 * `source` decides what sending MEANS on the server, not what this looks like:
 * 'prompt' closes the one-shot gate, 'menu' never does. The component only
 * passes it through.
 *
 * Sizing is written for 320px first. The dialog is a column that grows to
 * `min(440px, 100vw - 32px)`, so at 320 it is 288 wide with a 16px gutter on
 * each side and cannot be the thing that opens the viewport (T13's rule).
 */

const STARS = [1, 2, 3, 4, 5];
const MAX_COMMENT = 1000;

export default function FeedbackModal({ open, source, onClose }) {
  const { t } = useTranslation();
  const { user, setUser } = useAuth();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);
  const dialogRef = useRef(null);
  const lastFocused = useRef(null);

  // Fresh every time it opens: this modal is reachable again from the Menu, and
  // the second visit must not inherit the first one's stars.
  useEffect(() => {
    if (open) {
      setRating(0);
      setComment("");
      setSending(false);
    }
  }, [open]);

  // Focus goes in and comes back out. Escape closes, which for this modal means
  // exactly what "Not now" means — see `close` below.
  useEffect(() => {
    if (!open) return undefined;
    lastFocused.current = document.activeElement;
    const node = dialogRef.current;
    if (node) node.focus();
    const onKey = (e) => { if (e.key === "Escape") close(); };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      try { lastFocused.current?.focus?.(); } catch { /* gone from the DOM */ }
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  /** Mark the gate closed locally so nothing offers the prompt again before
   *  the next `/auth/me`. The server is the source of truth; this is the copy
   *  the guard reads in the meantime. */
  const markPromptDone = () => {
    setUser((current) => (current ? {
      ...current,
      profile: { ...(current.profile || {}), feedback_prompt_done: true },
    } : current));
  };

  const close = async () => {
    // "Not now" on the AUTOMATIC prompt has to persist, or the offer returns on
    // the next trigger and becomes the thing people complain about in it. From
    // the Menu there is nothing to persist: that door is always open.
    if (source === "prompt") {
      markPromptDone();
      try { await api.post("/feedback/dismiss"); } catch { /* the next /auth/me settles it */ }
    }
    onClose();
  };

  const send = async () => {
    if (!rating || sending) return;
    setSending(true);
    try {
      await api.post("/feedback", {
        rating,
        comment: comment.trim() || null,
        route: window.location.pathname,
        // `clientWidth` and not `innerWidth`: the same metre T13 settled on,
        // because `innerWidth` on a stretched viewport reports a width the
        // page does not have.
        viewport_width: document.documentElement.clientWidth,
        locale: (user?.profile?.language) || undefined,
        source,
      });
      if (source === "prompt") markPromptDone();
      toast.success(t("feedback.thanks"));
      onClose();
    } catch {
      // Deliberately silent beyond re-enabling the button. This is an optional
      // courtesy survey; an error banner about a failed opinion is a worse
      // experience than the missing opinion.
      setSending(false);
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-worker/60 p-4"
      onClick={close}
      data-testid="feedback-overlay"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={t("feedback.title")}
        data-testid="feedback-modal"
        onClick={(e) => e.stopPropagation()}
        className="bg-cream rounded-md shadow-deep p-5 outline-none"
        style={{ width: "min(440px, 100vw - 32px)" }}
      >
        <h2 className="font-display text-xl font-bold text-worker" style={{ margin: 0 }}>
          {t("feedback.title")}
        </h2>

        <div
          role="radiogroup"
          aria-label={t("feedback.title")}
          className="flex justify-center gap-1 mt-4"
          data-testid="feedback-stars"
        >
          {STARS.map((n) => (
            <button
              key={n}
              type="button"
              role="radio"
              aria-checked={rating === n}
              aria-label={String(n)}
              onClick={() => setRating(n)}
              // 44px of target, which is --touch-min: these are the only
              // controls on this dialog a thumb has to hit precisely.
              className="p-2 rounded-sm"
              style={{ minWidth: 44, minHeight: 44, background: "transparent", border: "none", cursor: "pointer" }}
              data-testid={`feedback-star-${n}`}
            >
              <Star
                size={28}
                className={n <= rating ? "text-hive-ink fill-hive" : "text-sand"}
              />
            </button>
          ))}
        </div>

        <textarea
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder={t("feedback.placeholder")}
          maxLength={MAX_COMMENT}
          className="input-base mt-4 min-h-[80px] w-full"
          data-testid="feedback-comment"
        />

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={close} className="fx-btn fx-btn--ghost" data-testid="feedback-dismiss">
            {t("feedback.dismiss")}
          </button>
          {/* Disabled until a star is picked: the comment is optional and the
              rating is the answer. Sending nothing would write a row that says
              nothing. */}
          <button
            type="button"
            onClick={send}
            disabled={!rating || sending}
            className="fx-btn fx-btn--primary"
            data-testid="feedback-submit"
          >
            {t("feedback.submit")}
          </button>
        </div>
      </div>
    </div>
  );
}
